import { useEffect, useRef } from 'react';

interface RefetchOnResumeOptions {
  /** Turn the listeners and the poll off entirely (e.g. nothing pending). */
  enabled?: boolean;
  /** Optional slow poll, only fires while the tab is visible. */
  intervalMs?: number;
}

/**
 * Self-healing for state that otherwise only arrives over a realtime socket.
 *
 * Mobile browsers close WebSockets when the phone locks or the fan switches
 * apps, and events that fired in the gap are gone for good. This re-reads the
 * truth from the server when the page comes back to the foreground, when the
 * network returns, and optionally on a slow timer while the state is moving.
 *
 * Always calls the latest `refetch` without re-subscribing on every render.
 */
export function useRefetchOnResume(
  refetch: () => unknown,
  { enabled = true, intervalMs }: RefetchOnResumeOptions = {},
) {
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!enabled) return;

    const run = () => {
      refetchRef.current();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', run);
    window.addEventListener('online', run);

    let timer: ReturnType<typeof setInterval> | undefined;
    if (intervalMs && intervalMs > 0) {
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') run();
      }, intervalMs);
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', run);
      window.removeEventListener('online', run);
      if (timer) clearInterval(timer);
    };
  }, [enabled, intervalMs]);
}
